// "View as organisation" for platform admins (2026-10-06; backend
// acting_client.py, routers/acting_client.py).
//
// - An "Organisation" dropdown at the top of the sidebar (platform_admin only)
//   lists every organisation with its data profile. Picking one stores the
//   choice on the SERVER session (POST /api/admin/acting-client) and reloads
//   the page, so every cached map payload, dataset list and store is fetched
//   again as that organisation (its grants, councils and P7a profile; a
//   public-only organisation shows the GIS view).
// - While viewing as another organisation a coloured banner sits at the top:
//   "Viewing as <org> — Return to <home org>", with the "Allow edits as this
//   organisation" toggle. Read-only by default: the server refuses changes
//   (403 acting_read_only) until the toggle is on. The toggle is audit-logged
//   and ends with the login session.
import { getActingClient, setActingClient } from "./api";
import type { ActingClientInfo, OrgChoice } from "./api";
import { getState, roleAtLeast } from "./state";
import { initMembershipSwitcher } from "./orgMemberships";
import { showToast } from "./toast";

function reloadAs(): void {
  // field PWA: drop cached API responses (sw.js) so nothing from the previous organisation is reused
  navigator.serviceWorker?.controller?.postMessage("clear-api-cache");
  window.location.reload();
}

async function switchTo(clientId: number | null, allowEdits = false): Promise<void> {
  try {
    await setActingClient(clientId, allowEdits);
    reloadAs();
  } catch (e) {
    showToast(`Couldn't switch organisation: ${e instanceof Error ? e.message : String(e)}`);
  }
}

function renderBanner(acting: ActingClientInfo, homeName: string): void {
  const header = document.getElementById("header");
  if (!header || document.getElementById("acting-banner")) return;
  const bar = document.createElement("div");
  bar.id = "acting-banner";
  bar.className = acting.allow_edits ? "acting-banner editing" : "acting-banner";
  bar.setAttribute("role", "status");

  const text = document.createElement("span");
  text.className = "acting-banner-text";
  const strong = document.createElement("strong");
  strong.textContent = acting.name ?? `organisation ${acting.id}`;
  const mode = document.createElement("span");
  mode.className = "acting-banner-mode";
  mode.textContent = acting.allow_edits ? "edits allowed" : "read-only";
  text.append("Viewing as ", strong, " — ", mode);

  const back = document.createElement("button");
  back.type = "button";
  back.className = "acting-banner-return";
  back.textContent = `Return to ${homeName}`;
  back.addEventListener("click", () => {
    back.disabled = true;
    void switchTo(null);
  });

  const toggleLabel = document.createElement("label");
  toggleLabel.className = "acting-banner-toggle";
  toggleLabel.title =
    "Changes are refused while viewing as another organisation. Turning this on lets you edit as this organisation " +
    "for this login session only; it is recorded in the audit log.";
  const toggle = document.createElement("input");
  toggle.type = "checkbox";
  toggle.checked = acting.allow_edits;
  toggle.addEventListener("change", () => {
    toggle.disabled = true;
    setActingClient(acting.id, toggle.checked)
      .then(() => {
        bar.className = toggle.checked ? "acting-banner editing" : "acting-banner";
        mode.textContent = toggle.checked ? "edits allowed" : "read-only";
        showToast(toggle.checked ? `Edits allowed as ${acting.name}` : `Back to read-only as ${acting.name}`);
      })
      .catch((e) => {
        toggle.checked = !toggle.checked;
        showToast(`Couldn't change the edit setting: ${e instanceof Error ? e.message : String(e)}`);
      })
      .finally(() => {
        toggle.disabled = false;
      });
  });
  toggleLabel.append(toggle, " Allow edits as this organisation");

  bar.append(text, toggleLabel, back);
  header.classList.add("acting");
  header.prepend(bar);
}

function renderDropdown(choices: OrgChoice[], effectiveId: number, homeId: number | null): void {
  const sidebar = document.getElementById("sidebar");
  if (!sidebar || document.getElementById("org-switcher")) return;
  const box = document.createElement("div");
  box.id = "org-switcher";
  box.className = "workspace-switcher org-switcher";
  const label = document.createElement("label");
  label.htmlFor = "org-select";
  label.textContent = "Organisation";
  const select = document.createElement("select");
  select.id = "org-select";
  select.title = "View the app as another organisation (platform admins). Read-only unless you allow edits.";
  for (const c of choices) {
    const opt = document.createElement("option");
    opt.value = String(c.id);
    const profile = c.gis_only ? "GIS view" : c.profile_kind === "full" ? "full" : (c.profile_label ?? "");
    opt.textContent = `${c.name}${c.id === homeId ? " (yours)" : ""}${profile ? ` · ${profile}` : ""}`;
    opt.selected = c.id === effectiveId;
    select.append(opt);
  }
  select.addEventListener("change", () => {
    const id = Number(select.value);
    select.disabled = true;
    void switchTo(id === homeId ? null : id);
  });
  box.append(label, select);
  const ws = document.getElementById("workspace-switcher");
  if (ws) ws.before(box);
  else sidebar.prepend(box);
}

/** Banner (anyone acting) + dropdown (platform admins). Call once at boot,
 * after the current user is known. Never throws. */
export async function initOrgSwitcher(): Promise<void> {
  const user = getState().currentUser;
  if (!user) return;
  const acting = user.acting_client ?? null;
  const homeName = user.home_client?.name ?? "your organisation";
  if (acting) renderBanner(acting, homeName);
  if (!roleAtLeast(user, "platform_admin")) {
    // multi-organisation members: home + memberships only (orgMemberships.ts)
    initMembershipSwitcher();
    return;
  }
  try {
    const info = await getActingClient();
    if (info.clients.length < 2) return;
    renderDropdown(info.clients, info.acting_client?.id ?? info.home_client?.id ?? 0, info.home_client?.id ?? null);
  } catch {
    // the dropdown is a convenience: leave it out rather than break boot
  }
}
